import React from 'react'
import styled from 'styled-components'

import { ExternalContent } from '@hozana/dom/components/ExternalContent'
import { useScreen } from '@hozana/screen/useScreen'
import { mediaQuery } from '@hozana/styling/responsive'
import { QA } from '@hozana/tests/constants'

import { Block } from 'elements/layout/Block'
import { Div } from 'elements/layout/Div'
import { Space } from 'elements/layout/Space'
import { H } from 'elements/text/H'
import { Separator } from 'elements/ui/Separator'

import { COMMUNITY_STATUS } from 'modules/community/constants'
import type { TCommunity } from 'modules/community/types'
import { CommunityTagLinksList } from 'modules/tag/components/CommunityTagLinksList'
import { useUser } from 'modules/user/hooks/useUser'

import { CommunityAdminForm } from '../CommunityAdminForm'
import { CommunitiesPropositionsBlock } from './CommunitiesPropositionsBlock'
import { CommunityPrayerBlock } from './CommunityPrayerBlock'
import { CommunitySharesMobile } from './CommunitySharesMobile'

export type TCommunityDescriptionTabProps = {
  community: TCommunity
}

const StyledH = styled(H)`
  font-weight: normal;

  ${mediaQuery('lg', 'xl')} {
    font-weight: bold;
  }
`

export const CommunityDescriptionTab: TFC<TCommunityDescriptionTabProps> = ({ community }) => {
  const me = useUser()
  const screen = useScreen()

  return (
    <>
      {me.admin && <CommunityAdminForm community={community} />}

      <Block
        role="tabpanel"
        data-testid={QA.COMMUNITY_PAGE.DESCRIPTION.WRAPPER}
        maxW={{ xs: 'initial', md: '712px', lg: 'initial' }}
        m={{ xs: '0 auto', md: '1em auto', lg: '0' }}
        p="20px 0 0 0"
        square
      >
        {/* Community description only visible on mobile */}
        <StyledH size="2" font="openSans" fontSize="1em" p="0 1em" data-testid={QA.COMMUNITY_PAGE.INTRO}>
          {community.introduction}
        </StyledH>
        <Space />
        <Div p="0 0 0 1em">
          <CommunityTagLinksList community={community} />
        </Div>
        <Separator m={{ xs: '1em 0', lg: '30px 0 1em' }} />
        {!(screen.lg || screen.xl) && ![COMMUNITY_STATUS.DRAFT, COMMUNITY_STATUS.CHECK].includes(community?.status) && (
          <Div visible={['xs', 'sm', 'md']}>
            <CommunitySharesMobile community={community} />
            <Separator m="1em 0" />
          </Div>
        )}
        <Div p="1em">
          <ExternalContent.Community content={community.description || ''} options={{ community }} />
        </Div>
      </Block>
      <CommunityPrayerBlock community={community} />
      <CommunitiesPropositionsBlock communities={community.communitiesPropositions} />
    </>
  )
}
